/**
 * Output Node Component
 *
 * Terminal node for saving workflow results.
 */

import { memo } from 'react';
import { type NodeProps } from '@xyflow/react';
import { BaseNode } from './BaseNode';
import { useI18n } from '@/i18n';
import type { NodeData } from '@/types/imageWorkflow';

const getArtifactTypeLabels = (t: any): Record<string, string> => ({
  lucky_charm: t('imageWorkflows.nodes.artifactTypes.lucky_charm'),
  soulmate: t('imageWorkflows.nodes.artifactTypes.soulmate'),
  soul_echo: t('imageWorkflows.nodes.artifactTypes.soul_echo'),
  daily_push: t('imageWorkflows.nodes.artifactTypes.daily_push'),
  natal_report: t('imageWorkflows.nodes.artifactTypes.natal_report'),
  transit_report: t('imageWorkflows.nodes.artifactTypes.transit_report'),
});

const OutputNodeComponent = ({ id, data, selected }: NodeProps) => {
  const { t } = useI18n();
  const nodeData = data as NodeData;
  const ARTIFACT_TYPE_LABELS = getArtifactTypeLabels(t);

  return (
    <BaseNode id={id} data={nodeData} type="output" selected={selected} hasSourceHandle={false}>
      <div className="space-y-1">
        {/* Artifact Type - Primary display */}
        {nodeData.artifact_type && (
          <div className="flex items-center gap-2">
            <span className="text-blue-500">{t('imageWorkflows.nodes.output.artifact')}</span>
            <span className="text-blue-700 font-medium">
              {ARTIFACT_TYPE_LABELS[nodeData.artifact_type as string] || nodeData.artifact_type}
            </span>
          </div>
        )}
        {/* Storage info */}
        <div className="flex items-center gap-2">
          <span className="text-gray-500">{t('imageWorkflows.nodes.output.storage')}</span>
          <span className="text-gray-700">
            {nodeData.artifact_type
              ? t('imageWorkflows.nodes.output.artifactUser')
              : nodeData.save_to_library
                ? t('imageWorkflows.nodes.output.libraryPermanent')
                : t('imageWorkflows.nodes.output.temp7days')}
          </span>
        </div>
        {nodeData.save_to_library && nodeData.category && !nodeData.artifact_type && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Category:</span>
            <span className="text-gray-700">{String(nodeData.category)}</span>
          </div>
        )}
        {nodeData.filename && (
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Filename:</span>
            <span className="text-gray-700 truncate max-w-[100px]">
              {String(nodeData.filename)}
            </span>
          </div>
        )}
      </div>
    </BaseNode>
  );
};

export const OutputNode = memo(OutputNodeComponent);
export default OutputNode;
